import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type RfqJobStatus = 'queued' | 'running' | 'succeeded' | 'failed'

interface StoredRfqJob {
  jobId: string
  tenderId: string
  status: RfqJobStatus
  model?: string
  createdAt: string
  updatedAt: string
  startedAt?: string
  completedAt?: string
  traceId?: string
  errorCode?: string
  errorMessage?: string
}

interface UpsertJobStatusPayload {
  jobId: string
  tenderId: string
  status: RfqJobStatus
  model?: string
  createdAt: string
  updatedAt?: string
  startedAt?: string
  completedAt?: string
  traceId?: string
  error?: {
    code?: string
    message?: string
  }
}

interface QueueJobPayload {
  jobId: string
  tenderId: string
  status?: RfqJobStatus
  model?: string
  createdAt: string
}

interface RfqJobsState {
  jobsByTender: Record<string, StoredRfqJob>
  setQueuedJob: (payload: QueueJobPayload) => void
  upsertJobStatus: (payload: UpsertJobStatusPayload) => void
  clearJob: (tenderId: string) => void
  pruneTerminalJobs: (maxAgeMs?: number) => void
}

const defaultTerminalRetentionMs = 1000 * 60 * 60 * 24

function toEpochMillis(isoLike?: string): number {
  if (!isoLike) return 0
  const parsed = Date.parse(isoLike)
  if (Number.isNaN(parsed)) return 0
  return parsed
}

export const useRfqJobsStore = create<RfqJobsState>()(
  persist(
    (set) => ({
      jobsByTender: {},

      setQueuedJob: (payload) =>
        set((state) => {
          if (!payload.tenderId || !payload.jobId) {
            return state
          }
          return {
            jobsByTender: {
              ...state.jobsByTender,
              [payload.tenderId]: {
                jobId: payload.jobId,
                tenderId: payload.tenderId,
                status: payload.status || 'queued',
                model: payload.model,
                createdAt: payload.createdAt,
                updatedAt: payload.createdAt,
              },
            },
          }
        }),

      upsertJobStatus: (payload) =>
        set((state) => {
          if (!payload.tenderId || !payload.jobId) {
            return state
          }
          const existing = state.jobsByTender[payload.tenderId]
          if (existing && existing.jobId !== payload.jobId) {
            const existingCreatedAt = toEpochMillis(existing.createdAt)
            const incomingCreatedAt = toEpochMillis(payload.createdAt)
            if (existingCreatedAt > incomingCreatedAt) {
              return state
            }
          }

          return {
            jobsByTender: {
              ...state.jobsByTender,
              [payload.tenderId]: {
                jobId: payload.jobId,
                tenderId: payload.tenderId,
                status: payload.status,
                model: payload.model || existing?.model,
                createdAt: payload.createdAt || existing?.createdAt || new Date().toISOString(),
                updatedAt: payload.updatedAt || new Date().toISOString(),
                startedAt: payload.startedAt || existing?.startedAt,
                completedAt: payload.completedAt || existing?.completedAt,
                traceId: payload.traceId || existing?.traceId,
                errorCode: payload.error?.code || existing?.errorCode,
                errorMessage: payload.error?.message || existing?.errorMessage,
              },
            },
          }
        }),

      clearJob: (tenderId) =>
        set((state) => {
          if (!state.jobsByTender[tenderId]) {
            return state
          }
          const next = { ...state.jobsByTender }
          delete next[tenderId]
          return { jobsByTender: next }
        }),

      pruneTerminalJobs: (maxAgeMs = defaultTerminalRetentionMs) =>
        set((state) => {
          const now = Date.now()
          const next: Record<string, StoredRfqJob> = {}
          for (const [tenderId, job] of Object.entries(state.jobsByTender)) {
            const isTerminal = job.status === 'succeeded' || job.status === 'failed'
            if (!isTerminal) {
              next[tenderId] = job
              continue
            }

            const terminalAt = toEpochMillis(job.completedAt || job.updatedAt || job.createdAt)
            if (!terminalAt || now-terminalAt <= maxAgeMs) {
              next[tenderId] = job
            }
          }
          return { jobsByTender: next }
        }),
    }),
    {
      name: 'gwiz-rfq-jobs',
      partialize: (state) => ({
        jobsByTender: state.jobsByTender,
      }),
    }
  )
)

if (typeof window !== 'undefined') {
  setTimeout(() => {
    useRfqJobsStore.getState().pruneTerminalJobs()
  }, 0)
}

